// Theme-only accessibility for the asynchronously rendered studio feed.
// Keep the app's own media, navigation, modal and cleanup handlers.
if (!customElements.get('pl-studio-feed')) {
  customElements.define('pl-studio-feed', class extends HTMLElement {
    connectedCallback() {
      this.onFeedClick = event => {
        const post = event.target.closest('[data-pl-studio-post]');
        if (post && this.contains(post)) this.opener = post;
      };
      this.onFeedKeyDown = event => {
        const post = event.target.closest('[data-pl-studio-post]');
        if (!post || event.target !== post || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        if (!event.repeat) post.click();
      };
      this.addEventListener('click', this.onFeedClick, true);
      this.addEventListener('keydown', this.onFeedKeyDown);
      this.refresh = () => {
        const content = this.querySelector('.Vtl-InstagramFeedLayout__Content');
        if (content && !content.id) content.id = this.id + '-posts';
        this.querySelectorAll('.Vtl-InstagramFeedLayout__Arrow').forEach(button => {
          if (!button.hasAttribute('aria-label')) {
            if (button.querySelector('[data-icon="chevron-left"]')) {
              button.setAttribute('aria-label', 'Previous studio photos');
            } else if (button.querySelector('[data-icon="chevron-right"]')) {
              button.setAttribute('aria-label', 'Next studio photos');
            }
          }
          if (content) button.setAttribute('aria-controls', content.id);
        });
        const posts = this.querySelectorAll('.Vtl-InstagramFeedLayout__Post');
        posts.forEach((post, index) => {
          // Leave future native app controls to their built-in keyboard behavior.
          if (post.matches('a,button') || post.querySelector('a,button')) return;
          post.setAttribute('role', 'button');
          post.setAttribute('tabindex', '0');
          post.setAttribute('aria-label', `Open studio post ${index + 1} of ${posts.length}`);
          post.setAttribute('aria-haspopup', 'dialog');
          post.setAttribute('data-pl-studio-post', '');
        });
        this.updateViewer();
      };
      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this, {childList: true, subtree: true, attributes: true, attributeFilter: ['open']});
      this.refresh();
    }
    visibleCloseControl() {
      if (!this.viewer) return null;
      return Array.from(this.viewer.querySelectorAll('.Vtl-InstagramFeedModal__CloseModalButton, .Vtl-InstagramFeedModal__BackButtonMobile'))
        .find(control => control.getClientRects().length && getComputedStyle(control).visibility !== 'hidden');
    }
    closeViewer() {
      const close = this.visibleCloseControl();
      if (!close) return false;
      close.click(); // The app also removes scroll locks and stops its media.
      return true;
    }
    updateViewer() {
      const next = this.querySelector('dialog.Vtl-InstagramFeedModal[open]');
      if (this.viewer && this.viewer !== next) this.releaseViewer(!next);
      if (!next) return;
      const firstOpen = next !== this.viewer;
      this.viewer = next;
      next.setAttribute('aria-label', 'Studio Instagram post viewer');
      const controls = [
        ['.Vtl-InstagramFeedModal__CloseModalButton, .Vtl-InstagramFeedModal__BackButtonMobile', 'Close studio post'],
        ['.Vtl-InstagramFeedModal__SliderArrow--Prev', 'Previous studio post'],
        ['.Vtl-InstagramFeedModal__SliderArrow--Next', 'Next studio post']
      ];
      controls.forEach(([selector, label]) => next.querySelectorAll(selector).forEach(control => {
        control.setAttribute('aria-label', label);
        if (!control.matches('a,button')) {
          control.setAttribute('role', 'button');
          control.setAttribute('tabindex', '0');
          control.setAttribute('data-pl-viewer-control', '');
        }
      }));
      if (!firstOpen) return;
      this.onViewerKeyDown = event => {
        if (event.key === 'Tab') {
          // Keep native dialog/video tab traversal; the app's bubbling handler
          // otherwise consumes Tab even on its original profile links.
          event.stopImmediatePropagation();
        } else if (event.key === 'Escape') {
          if (document.fullscreenElement || !this.visibleCloseControl()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closeViewer();
        } else if (['Enter', ' '].includes(event.key) && event.target.matches('[data-pl-viewer-control]')) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) event.target.click();
        }
      };
      this.onViewerCancel = event => {
        if (!this.visibleCloseControl()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeViewer();
      };
      next.addEventListener('keydown', this.onViewerKeyDown, true);
      next.addEventListener('cancel', this.onViewerCancel, true);
      this.focusFrame = requestAnimationFrame(() => {
        if (this.viewer === next && next.open) this.visibleCloseControl()?.focus({preventScroll: true});
      });
    }
    releaseViewer(restoreFocus) {
      cancelAnimationFrame(this.focusFrame);
      this.viewer?.removeEventListener('keydown', this.onViewerKeyDown, true);
      this.viewer?.removeEventListener('cancel', this.onViewerCancel, true);
      this.viewer = null;
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({preventScroll: true});
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.onFeedClick, true);
      this.removeEventListener('keydown', this.onFeedKeyDown);
      this.releaseViewer(false);
    }
  });
}
